import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { LabelSize } from './label-template.store';
import { PrintProtocol } from '../services/label-print.service';
import { supabase } from '../services/supabase';

export interface PrintScenario {
    id: string;
    name: string;
    icon: string;
    description?: string;
    templateId: string;
    printSize: LabelSize;
    protocol: PrintProtocol;
    copies: number;
    isEnabled: boolean;
}

interface PrintScenarioState {
    scenarios: PrintScenario[];
    activeScenarioId: string | null;

    // Database sync actions
    fetchFromDb: () => Promise<void>;
    saveToDb: (scenario: PrintScenario) => Promise<void>;
    deleteFromDb: (id: string) => Promise<void>;

    // Actions
    addScenario: (scenario: PrintScenario) => void;
    updateScenario: (id: string, updates: Partial<PrintScenario>) => void;
    deleteScenario: (id: string) => void;
    setActiveScenario: (id: string | null) => void;
    getActiveScenario: () => PrintScenario | null;
    initDefaults: () => Promise<void>;
}

const DEFAULT_SCENARIOS: PrintScenario[] = [
    {
        id: 'scenario_standard',
        name: '标准面单',
        icon: 'description',
        description: '76x130mm 标准物流面单',
        templateId: 'default_76x130',
        printSize: '76x130',
        protocol: 'auto',
        copies: 1,
        isEnabled: true,
    },
    {
        id: 'scenario_small_tag',
        name: '极简小标',
        icon: 'label',
        description: '40x30mm 紧凑型包裹标签',
        templateId: 'default_40x30',
        printSize: '40x30',
        protocol: 'auto',
        copies: 1,
        isEnabled: true,
    },
    {
        id: 'scenario_warehouse',
        name: '仓储入库',
        icon: 'store',
        description: '100x100mm 入库标识单',
        templateId: 'default_100x100',
        printSize: '100x100',
        protocol: 'auto',
        copies: 1,
        isEnabled: true,
    }
];

export const usePrintScenarioStore = create<PrintScenarioState>()(
    persist(
        (set, get) => ({
            scenarios: DEFAULT_SCENARIOS,
            activeScenarioId: 'scenario_standard',

            fetchFromDb: async () => {
                const { data: userData } = await supabase.auth.getUser();
                if (!userData?.user) return;
                const { data, error } = await supabase
                    .from('print_scenarios')
                    .select('*')
                    .eq('user_id', userData.user.id);
                if (!error && data && data.length > 0) {
                    const dbScenarios: PrintScenario[] = data.map(row => ({
                        id: row.id,
                        name: row.name,
                        icon: row.icon,
                        description: row.description,
                        templateId: row.template_id,
                        printSize: row.print_size as LabelSize,
                        protocol: row.protocol as PrintProtocol,
                        copies: row.copies,
                        isEnabled: row.is_enabled
                    }));
                    set({ scenarios: dbScenarios });
                }
            },

            saveToDb: async (scenario) => {
                const { data: userData } = await supabase.auth.getUser();
                if (!userData?.user) return;
                await supabase.from('print_scenarios').upsert({
                    id: scenario.id,
                    user_id: userData.user.id,
                    name: scenario.name,
                    icon: scenario.icon,
                    description: scenario.description,
                    template_id: scenario.templateId,
                    print_size: scenario.printSize,
                    protocol: scenario.protocol,
                    copies: scenario.copies,
                    is_enabled: scenario.isEnabled,
                    updated_at: new Date().toISOString()
                }, { onConflict: 'id' });
            },

            deleteFromDb: async (id) => {
                await supabase.from('print_scenarios').delete().eq('id', id);
            },

            addScenario: (scenario) => {
                set((s) => ({ scenarios: [...s.scenarios, scenario] }));
                get().saveToDb(scenario);
            },

            updateScenario: (id, updates) => {
                set((s) => ({
                    scenarios: s.scenarios.map((sc) => sc.id === id ? { ...sc, ...updates } : sc)
                }));
                const updated = get().scenarios.find(sc => sc.id === id);
                if (updated) get().saveToDb(updated);
            },

            deleteScenario: (id) => {
                set((s) => ({
                    scenarios: s.scenarios.filter((sc) => sc.id !== id),
                    activeScenarioId: s.activeScenarioId === id ? null : s.activeScenarioId
                }));
                get().deleteFromDb(id);
            },

            setActiveScenario: (id) => set({ activeScenarioId: id }),

            getActiveScenario: () => {
                const { scenarios, activeScenarioId } = get();
                return scenarios.find(s => s.id === activeScenarioId) || scenarios[0] || null;
            },

            initDefaults: async () => {
                await get().fetchFromDb();
                const { scenarios } = get();
                // Merge default scenarios if not exist locally
                const missingDefaults = DEFAULT_SCENARIOS.filter(
                    def => !scenarios.some(sc => sc.id === def.id)
                );

                if (missingDefaults.length > 0) {
                    set((s) => ({
                        scenarios: [...s.scenarios, ...missingDefaults],
                        activeScenarioId: s.activeScenarioId || 'scenario_standard'
                    }));
                    // push to db
                    missingDefaults.forEach(sc => get().saveToDb(sc));
                }
            }
        }),
        {
            name: 'hedui-print-scenarios',
            storage: createJSONStorage(() => localStorage),
        }
    )
);
